import React from 'react';
import Dialog from '../../../../../../../BaseComponents/Dialog/EDDialog';
import { IFilledLocalAnalys } from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import {
  ExcelQuery,
  IExcelData,
  getPeriodTypeFromDateInputType,
} from '../Excel/Queries/ExcelQuery';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../EkonomiskAnalysPage';
import EDCheckbox from '../../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import { convertUIUrvalToTEDUrval } from '../../../../../../../Components/Urval/Utils/convertUIUrvalToTEDUrval';
import { datesToTimeInterval } from '../../Utils/convertUtils';
import { getServerURL } from '../../../../../../../Utils/getServerURL';
import EDDivider from '../../../../../../../BaseComponents/Divider/EDDivider';
import EkonomiskAnalysExportDetailsSettings, {
  firstVisibleColIndex,
} from '../EkonomiskAnalysExport/Components/EkonomiskAnalysExportDetailsSettings/EkonomiskAnalysExportDetailsSettings';
import EkonomiskAnalysExportSumSettings from '../EkonomiskAnalysExport/Components/EkonomiskAnalysExportSumSettings/EkonomiskAnalysExportSumSettings';
import EDOverlay from '../../../../../../../BaseComponents/Overlay/EDOverlay';
import { equals } from 'ramda';
import EkonomiskAnalysExportToolbar from '../EkonomiskAnalysExportToolbar/EkonomiskAnalysExportToolbar';
import {
  ekonomiskAnalysDatesToDateString,
  DateInputType,
} from '../../../../../../../Utils/dateUtils';
import { IsPeriodLockedFnType } from '../../../../../../../Components/BokfaringsarActions/BokforingsarActions';
import EDAvenyProgress from '../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import EDBox from '../../../../../../../BaseComponents/EDBox/EDBox';
import { getComparisonString } from '../../Utils/analysUtils';

interface IProps {
  open: boolean;
  selectedAnalys: ISelectedAnalys;
  radbaserad: boolean;
  analysSettings: IFilledLocalAnalys;
  onClose: () => void;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  isPeriodLocked: IsPeriodLockedFnType;
  forceHelarsjamforelse: boolean;
  classes?: any;
}

interface IState {
  includeSummation: boolean;
  sumLevel: number;
  showZeros: boolean;
  delakodben: boolean;
  includeDetails: boolean;
  detailColumnIndex: number;
  loading: boolean;
}

const styles = (theme: Theme) => ({
  dialogPaper: {
    minWidth: 400,
  },
  otherSettingsContainer: {
    marginTop: theme.spacing(2),
  },
  savingOverlay: {
    right: 'calc(50% - 20px)',
    bottom: 'calc(50% - 20px)',
  },
});

const defaultOptions = {
  sumLevel: 0,
  includeSummation: false,
  showZeros: false,
  delakodben: false,
  includeDetails: false,
};

/**
 * En dialog som visar inställningar för en anlys excel utrskrift.
 */
export class EkonomiskAnalysExcelModal extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      sumLevel: defaultOptions.sumLevel,
      includeSummation: defaultOptions.includeSummation,
      showZeros: defaultOptions.showZeros,
      delakodben: defaultOptions.delakodben,
      includeDetails: defaultOptions.includeDetails,
      detailColumnIndex: firstVisibleColIndex(props.analysSettings.columns),
      loading: false,
    };
  }

  componentDidUpdate = (prevProps: IProps, prevState: IState) => {
    // Vill glömma inställningar om vi har bytt analys, eller detailColumnIndex inte blivit satt (vilket sker i första renderingen)
    // eller vi ändrat summor eller kolumner
    if (
      (prevProps.open !== this.props.open &&
        this.props.open &&
        prevState.detailColumnIndex === -1) ||
      !equals(prevProps.selectedAnalys, this.props.selectedAnalys) ||
      !equals(prevProps.analysSettings.sums, this.props.analysSettings.sums) ||
      !equals(
        prevProps.analysSettings.columns,
        this.props.analysSettings.columns
      )
    ) {
      this.reset();
    }
    if (prevState.loading && !this.state.loading) {
      this.props.onClose();
    }
  };

  toggleIncludeSummation = (checked: boolean) => {
    this.setState({
      includeSummation: checked,
    });
  };

  setSumLevel = (level: number) => {
    this.setState({
      sumLevel: level,
    });
  };

  setShowZeros = (checked: boolean) => {
    this.setState({
      showZeros: checked,
    });
  };

  setIncludeDetails = (checked: boolean) => {
    this.setState({
      includeDetails: checked,
    });
  };

  setDetailColumnIndex = (newIndex: number) => {
    this.setState({
      detailColumnIndex: newIndex,
    });
  };

  toggleTableMenuLoading = () => {
    this.setState(prevState => ({
      loading: !prevState.loading,
    }));
  };

  setDelakodben = (checked: boolean) => {
    this.setState({
      delakodben: checked,
    });
  };

  printExcel = () => {
    if (this.props.analysSettings && this.props.selectedAnalys) {
      const nrDecimals = this.props.analysSettings.other.decimals;
      const denomation =
        this.props.analysSettings.other.denomination.toString();
      const excelData: IExcelData = {
        analys: this.props.radbaserad
          ? undefined
          : {
              name: this.props.analysSettings.name,
              rubrik: this.props.analysSettings.title,
              kategori: this.props.analysSettings.category,
              columns: this.props.analysSettings.columns
                .filter(col => !col.data.HIDE_COLUMN)
                .map(c => ({
                  ...c.data,
                  ANTAL_DECIMALER: nrDecimals,
                  BAS: denomation,
                })),
              sums: this.props.analysSettings.sums.slice(
                0,
                this.state.sumLevel + 1
              ),
              endast_bokfort:
                !this.props.analysSettings.other.includeEjBokfordaJournaler,
              force_helarsjamforelse: this.props.forceHelarsjamforelse,
            },
        force_helarsjamforelse: this.props.forceHelarsjamforelse,
        kodbeh_id: this.props.impersonatedCodePrivilege
          ? this.props.impersonatedCodePrivilege.id
          : this.props.selectedAnalys.codePrivilege.id,
        grundurval_id: this.props.selectedAnalys.analys.grundurval
          ? this.props.selectedAnalys.analys.grundurval.id
          : undefined,
        analysid: this.props.selectedAnalys.analys.id,
        tidsintervall: datesToTimeInterval(this.props.analysSettings.dates),
        anvurval_selection: this.props.analysSettings.urval
          ? JSON.stringify(
              convertUIUrvalToTEDUrval(this.props.analysSettings.urval)
            )
          : undefined,
        detaljurval_selection: this.props.analysSettings.detaljUrval
          ? JSON.stringify(
              convertUIUrvalToTEDUrval(this.props.analysSettings.detaljUrval)
            )
          : undefined,
        sumniva: 1, // 1 = totalsumma
        visanoll: this.state.showZeros,
        delakodben: this.state.delakodben,
        visadetaljer: this.state.includeDetails,
        detaljkolumn: this.state.detailColumnIndex,
        summerings_mode:
          this.props.analysSettings.other.summationMode.backendValue,
        budget_summerings_mode:
          this.props.analysSettings.other.budgetSummationMode.backendValue,
        base: this.props.analysSettings.other.denomination,
        decimaler: this.props.analysSettings.other.decimals,
        periodType: getPeriodTypeFromDateInputType(
          this.props.analysSettings.dateInputType ?? DateInputType.year
        ),
        jamforelsetal: getComparisonString(
          this.props.analysSettings.dates,
          this.props.analysSettings.isInterval,
          this.props.analysSettings.dateInputType,
          this.props.forceHelarsjamforelse
        ),
        fromPerString: ekonomiskAnalysDatesToDateString(
          this.props.analysSettings.dates,
          this.props.analysSettings.dateInputType,
          false
        ),
        tomPerString: this.props.analysSettings.isInterval
          ? ekonomiskAnalysDatesToDateString(
              this.props.analysSettings.dates,
              this.props.analysSettings.dateInputType,
              true
            )
          : null,
        periodLast: this.props.isPeriodLocked(
          this.props.analysSettings.dates.from.year,
          this.props.analysSettings.dates.from.month,
          this.props.analysSettings.dates.to.month,
          this.props.analysSettings.dateInputType
        ),
      };
      ExcelQuery(
        excelData,
        this.props.analysSettings.name,
        getServerURL(),
        'EkonomiskAnalys',
        this.toggleTableMenuLoading
      );
    }
  };

  reset = () => {
    this.setSumLevel(defaultOptions.sumLevel);
    this.setIncludeDetails(defaultOptions.includeDetails);
    this.setDetailColumnIndex(
      firstVisibleColIndex(this.props.analysSettings.columns)
    );
    this.setShowZeros(defaultOptions.showZeros);
    this.setDelakodben(defaultOptions.delakodben);
  };

  render() {
    const { dialogPaper, otherSettingsContainer } = this.props.classes;

    return (
      <Dialog
        title={'Exportera till excel'}
        open={this.props.open}
        onClose={this.props.onClose}
        fullWidth={false}
        footerActions={[
          { label: 'Avbryt', onClick: this.props.onClose },
          { label: 'Exportera', onClick: () => this.printExcel() },
        ]}
        classes={{ paper: dialogPaper }}
        headerTools={<EkonomiskAnalysExportToolbar reset={this.reset} />}
      >
        <EDOverlay
          overlay={this.state.loading && <EDAvenyProgress />}
          classes={{ overlay: this.props.classes.savingOverlay }}
        >
          <div style={{ margin: '20px 0' }}>
            {!this.props.radbaserad && (
              <EkonomiskAnalysExportSumSettings
                analysSettings={this.props.analysSettings}
                sumLevel={this.state.sumLevel}
                setSumLevel={this.setSumLevel}
              />
            )}
            <EDDivider />
            <EkonomiskAnalysExportDetailsSettings
              analysSettings={this.props.analysSettings}
              selectedAnalys={this.props.selectedAnalys}
              includeDetails={this.state.includeDetails}
              setIncludeDetails={this.setIncludeDetails}
              detailColumnIndex={this.state.detailColumnIndex}
              setDetailColumnIndex={this.setDetailColumnIndex}
              radbaserad={this.props.radbaserad}
            />
            <EDDivider />
            <div className={otherSettingsContainer}>
              <EDBox>
                <EDCheckbox
                  label="Visa nollbelopp"
                  checked={this.state.showZeros}
                  onCheck={this.setShowZeros}
                  checkboxSpacing={{ paddingY: 0.5 }}
                />
              </EDBox>
              <EDBox>
                <EDCheckbox
                  label="Separera kod och benämning"
                  checked={this.state.delakodben}
                  onCheck={this.setDelakodben}
                  checkboxSpacing={{ paddingY: 0.5 }}
                />
              </EDBox>
            </div>
          </div>
        </EDOverlay>
      </Dialog>
    );
  }
}

export default withStyles(EkonomiskAnalysExcelModal, styles);
